import { DndContext, DragOverlay } from '@dnd-kit/core'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import zIndex from 'styles/tools/z-index'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Switch from 'LuxKit/Switch/Switch'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import type { TripStop } from 'tripPlanner/api/types'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import LineArrowUpIcon from 'LuxKit/Icons/line/LineArrowUpIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import StopSearchActions from './StopSearchActions'
import LocationFilterMenuItem from './LocationFilterMenuItem'
import TripListRowItem from './TripListRowItem'
import StatusFilterMenuItem from './StatusFilterMenuItem'
import TypeFilterMenuItem from './TypeFilterMenuItem'
import { TRIP_LIST_GRID_COLUMNS, TRIP_LIST_MIN_WIDTH } from './tripListLayout'
import {
  canMoveTripListRowToAnotherDay,
  getTripListBulkMoveDayOptions,
  type TripListMoveDayOption,
} from './tripListMoveDayOptions'
import TripListQuickActions from './TripListQuickActions'
import { getTripListTypeLabel } from './tripListTypeLabels'
import { getTripListFooterTotalPrice } from './tripListPrices'
import { getTripListStatusLabel } from './tripListStatus'
import StopTimingEditControl from '../StopTimingEditControl'
import {
  getTripListDateFilterOptions,
  getTripListLocationFilterOptions,
  getTripListRowCountByStatus,
  getTripListRowCountByType,
  getTripListStatusFilterOptions,
  getTripListTypeFilterOptions,
  hasTripListBookableOnlyFilterOption,
  hasTripListDateFilterOptions,
  hasTripListLocationFilterOptions,
  hasTripListStatusFilterOptions,
  hasTripListTypeFilterOptions,
} from './tripListFilterOptions'
import { useCloseDropdownWhenUnavailable } from './useCloseDropdownWhenUnavailable'
import type { ItineraryScrollTarget } from '../../hooks/useItineraryUpdates'
import { useItineraryScrollTarget } from '../../hooks/useItineraryScrollTarget'
import WhileDraggingStyle from 'tripPlanner/components/DragAndDrop/WhileDraggingStyle'
import { DROP_ANIMATION_DURATION, autoScrollConfig, dropAnimationSideEffects } from './tripListReorder'
import { useTripListDragReorder } from './useTripListDragReorder'
import type {
  TripListFilters,
  TripListGroup,
  TripListRow,
  TripListRowType,
  TripListSort,
  TripListSortField,
  TripListStatusValue,
} from './tripListTypes'

const TRIP_LIST_SCROLL_SELECTORS = {
  item: (target: ItineraryScrollTarget) => target.itemId !== undefined ? `[data-item-id="${target.itemId}"]` : undefined,
  day: (target: ItineraryScrollTarget) => target.dayId !== undefined ? `[data-day-id="${target.dayId}"]` : undefined,
  stop: (target: ItineraryScrollTarget) => `[data-stop-id="${target.stopId}"]`,
}

const ListPanel = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const Toolbar = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(16)};
  width: 100%;
  min-width: 0;
  padding: ${rem(20)} 0 ${rem(16)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  flex-shrink: 0;
`

const ToolbarControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.L};
`

const FilterChips = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  flex-wrap: wrap;
  gap: ${rem(8)};
  min-width: 0;
  overflow: visible;
`

const LuxPlusToggle = styled.label`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  height: ${rem(32)};
  padding: 0 ${rem(10)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  &:has(:disabled) {
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const ClearFiltersButton = styled(TextButton)`
  && {
    height: ${rem(32)};
    padding-inline: ${rem(10)};
    border-radius: ${props => props.theme.borderRadius.round};
  }
`

const ScrollArea = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  padding: 0 ${rem(20)} ${rem(12)};
`

const FilterChipButton = styled.button`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(8)};
  min-width: ${rem(72)};
  height: ${rem(32)};
  padding: 0 ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  &:disabled,
  &:disabled:hover {
    cursor: default;
    border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

    svg {
      color: currentColor;
    }
  }
`

const FilterChipText = styled.span`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  font-weight: 500;
`

const FilterDropdownList = styled(DropdownList)`
  > div > div {
    padding-bottom: 0;
  }
`

const TypeFilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: ${rem(256)};
  max-height: ${rem(360)};
`

const TypeFilterHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: ${rem(4)} ${rem(16)} ${rem(2)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  flex-shrink: 0;
`

const TypeFilterContent = styled.div`
  overflow-y: auto;
  padding: 0 ${rem(16)};
`

const TypeFilterOptions = styled.div`
  margin-top: ${rem(4)};
`

const TypeFilterFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(16)};
  padding: ${rem(16)} ${rem(16)} 0;
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  flex-shrink: 0;
`

const ClearFilterButton = styled(TextButton)`
  min-width: 0;
  padding-inline: 0;
`

const DragOverlayContent = styled.div`
  opacity: 0.68;
  pointer-events: none;
`

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  width: 100%;
  min-width: ${TRIP_LIST_MIN_WIDTH};
`

const DayGroup = styled.section`
  background: ${props => props.theme.palette.neutral.surface.lightBackground};

  &:first-child {
    border-start-start-radius: ${props => props.theme.borderRadius.L};
    border-start-end-radius: ${props => props.theme.borderRadius.L};
  }

  &:last-child {
    border-end-end-radius: ${props => props.theme.borderRadius.L};
    border-end-start-radius: ${props => props.theme.borderRadius.L};
  }
`

const StopHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${rem(16)};
  min-height: ${rem(44)};
  padding: ${rem(10)} ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.darkBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  border-start-start-radius: ${props => props.theme.borderRadius.L};
  border-start-end-radius: ${props => props.theme.borderRadius.L};
`

const StopHeaderText = styled.div`
  display: flex;
  align-items: baseline;
  min-width: 0;
  gap: ${rem(8)};
`

const StopHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  margin-inline-start: auto;
  flex-shrink: 0;
`

const StopHeaderTitle = styled(BodyText)`
  min-width: 0;
  flex: 0 1 auto;
  overflow-wrap: anywhere;
`

const StopHeaderSubtitle = styled(Caption)`
  min-width: 0;
  flex: 1 0 auto;
  color: inherit;
  opacity: 0.82;
`

const EmptyStopPlaceholder = styled.div`
  display: grid;
  grid-template-columns: ${TRIP_LIST_GRID_COLUMNS};
  grid-template-areas: "select meta title status price action";
  min-height: ${rem(52)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const EmptyStopPlaceholderText = styled.div`
  grid-column: 1 / 4;
  display: flex;
  align-items: center;
  min-width: 0;
  padding: ${rem(14)} ${rem(20)};
`

const EmptyStopActions = styled.div`
  grid-area: action;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(8)};
  min-width: 0;
  padding-block: ${rem(10)};
  padding-inline: ${rem(8)} ${rem(36)};
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  margin: 0;
  padding: ${rem(16)} ${rem(20)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  min-width: 0;
  flex-shrink: 0;
`

const FooterSummary = styled(Group)`
  min-width: 0;
`

const FooterActions = styled(Group)`
  flex-shrink: 0;
`

const CountChip = styled.span`
  padding: ${rem(2)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(200)};
  padding: ${rem(24)};
`

const BookAllButton = styled(TextButton)`
  border-radius: ${props => props.theme.borderRadius.round};
`

export interface TripListViewProps {
  groups: Array<TripListGroup>
  tripStops: Array<TripStop>
  allRows: Array<TripListRow>
  visibleRows: Array<TripListRow>
  selectedRows: Array<TripListRow>
  selectedRowIds: Set<string>
  filters: TripListFilters
  sort: TripListSort | undefined
  luxPlusEnabled: boolean
  onSelectAll: (checked: boolean) => void
  onSelectRow: (rowId: string, checked: boolean) => void
  onDateFiltersChange: (dates: Set<string>) => void
  onLocationFiltersChange: (locations: Set<string>) => void
  onTypeFiltersChange: (types: Set<TripListRowType>, bookableOnly: boolean) => void
  onStatusFiltersChange: (statuses: Set<TripListStatusValue>) => void
  onClearFilters: () => void
  onSortToggle: (field: TripListSortField) => void
  onLuxPlusToggle: () => void
  onRowClick: (row: TripListRow) => void
  onBookRow: (row: TripListRow) => void
  onBookAll: () => void
  onViewBooking: (row: TripListRow) => void
  onAskAi: (row: TripListRow, origin?: { x: number; y: number }) => void
  onAskAiMove: (row: TripListRow, origin?: { x: number; y: number }) => void
  onAskAiSelectedRows: (origin?: { x: number; y: number }) => void
  onAskAiMoveSelectedRows: (origin?: { x: number; y: number }) => void
  onDelete: (row: TripListRow) => void
  onMoveToDay: (row: TripListRow, option: TripListMoveDayOption) => void | boolean | Promise<void | boolean>
  onMoveSelectedRowsToDay: (option: TripListMoveDayOption) => void | Promise<void>
  onDeleteSelectedRows: () => Promise<void>
  onClearSelection: () => void
  onOpenStopHotels: (stop: TripStop) => void
  onOpenStopFlights: (stop: TripStop) => void
  onOpenStopExperiences: (stop: TripStop) => void
  onSendStopTimingEditMessage: (message: string) => Promise<void> | void
  onUpdateNote: (row: TripListRow, notes: string | undefined) => void
  onUpdateStatus: (row: TripListRow, status: TripListStatusValue) => void
  bookingRowId?: string
  noteUpdateRowId?: string
  statusUpdateRowId?: string
  notesDisabled?: boolean
  statusDisabled?: boolean
  actionsDisabled?: boolean
  scrollTarget?: ItineraryScrollTarget
  onScrollComplete?: () => void
}

function getSortIcon(sort: TripListSort | undefined, field: TripListSortField) {
  if (sort?.field !== field) return <LineAngleDownIcon size="XS" />
  return sort.direction === 'asc' ? <LineArrowUpIcon size="XS" /> : <LineArrowDownIcon size="XS" />
}

function TripListView({
  groups,
  tripStops,
  allRows,
  visibleRows,
  selectedRows,
  selectedRowIds,
  filters,
  sort,
  luxPlusEnabled,
  onSelectRow,
  onDateFiltersChange,
  onLocationFiltersChange,
  onTypeFiltersChange,
  onStatusFiltersChange,
  onClearFilters,
  onSortToggle,
  onLuxPlusToggle,
  onRowClick,
  onBookRow,
  onBookAll,
  onViewBooking,
  onAskAi,
  onAskAiMove,
  onAskAiSelectedRows,
  onAskAiMoveSelectedRows,
  onDelete,
  onMoveToDay,
  onMoveSelectedRowsToDay,
  onDeleteSelectedRows,
  onClearSelection,
  onOpenStopHotels,
  onOpenStopFlights,
  onOpenStopExperiences,
  onSendStopTimingEditMessage,
  onUpdateNote,
  onUpdateStatus,
  bookingRowId,
  noteUpdateRowId,
  statusUpdateRowId,
  notesDisabled = false,
  statusDisabled = false,
  actionsDisabled = false,
  scrollTarget,
  onScrollComplete,
}: TripListViewProps) {
  const intl = useIntl()
  const [locationFilterOpen, setLocationFilterOpen] = useState(false)
  const [dateFilterOpen, setDateFilterOpen] = useState(false)
  const [typeFilterOpen, setTypeFilterOpen] = useState(false)
  const [statusFilterOpen, setStatusFilterOpen] = useState(false)
  const dateFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const locationFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const typeFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const statusFilterTriggerRef = useRef<HTMLButtonElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  useItineraryScrollTarget({
    scrollTarget,
    containerRef: scrollAreaRef,
    selectors: TRIP_LIST_SCROLL_SELECTORS,
    onScrollComplete,
  })

  const bookableRows = useMemo(() => visibleRows.filter(row => row.isBookable && !row.isBooked), [visibleRows])
  const hasVisibleRows = visibleRows.length > 0
  const footerTotal = useMemo(() => getTripListFooterTotalPrice(bookableRows.map(row => row.price), luxPlusEnabled), [bookableRows, luxPlusEnabled])
  const selectedRowsCanMoveToDay = selectedRows.length > 0 && selectedRows.every(canMoveTripListRowToAnotherDay)
  const selectedMoveDayOptions = useMemo(
    () => selectedRowsCanMoveToDay ? getTripListBulkMoveDayOptions(selectedRows, tripStops, intl) : [],
    [intl, selectedRows, selectedRowsCanMoveToDay, tripStops],
  )
  const showSelectedMoveWithAi = selectedRows.length > 0 && !selectedRowsCanMoveToDay
  const activeDateFilterCount = filters.dates.size
  const hasActiveDateFilter = activeDateFilterCount > 0
  const activeLocationFilterCount = filters.locations.size
  const hasActiveLocationFilter = activeLocationFilterCount > 0
  const activeTypeFilterCount = filters.types.size + (filters.bookableOnly ? 1 : 0)
  const hasActiveTypeFilter = activeTypeFilterCount > 0
  const activeStatusFilterCount = filters.statuses.size
  const hasActiveStatusFilter = activeStatusFilterCount > 0
  const hasActiveSort = Boolean(sort)
  const hasActiveFilters = hasActiveDateFilter || hasActiveLocationFilter || hasActiveTypeFilter || hasActiveStatusFilter || filters.bookableOnly || hasActiveSort
  const dragActionsDisabled = actionsDisabled || hasActiveFilters
  const {
    orderedRenderableGroups,
    activeRenderableRow,
    overDragRowId,
    dropTargetPlacement,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
  } = useTripListDragReorder({
    groups,
    visibleRows,
    selectedRowIds,
    bookingRowId,
    noteUpdateRowId,
    statusUpdateRowId,
    dragActionsDisabled,
    onMoveToDay,
  })
  const dateFilterOptions = useMemo(() => getTripListDateFilterOptions(allRows), [allRows])
  const hasDateFilterOptions = hasTripListDateFilterOptions(allRows)
  const locationFilterOptions = useMemo(() => getTripListLocationFilterOptions(allRows), [allRows])
  const hasLocationFilterOptions = hasTripListLocationFilterOptions(allRows)
  const rowCountByType = useMemo(() => getTripListRowCountByType(allRows), [allRows])
  const typeFilterOptions = useMemo(() => getTripListTypeFilterOptions(rowCountByType), [rowCountByType])
  const hasTypeFilterOptions = hasTripListTypeFilterOptions(allRows)
  const hasBookableOnlyFilterOption = hasTripListBookableOnlyFilterOption(allRows)
  const rowCountByStatus = useMemo(() => getTripListRowCountByStatus(allRows), [allRows])
  const statusFilterOptions = useMemo(() => getTripListStatusFilterOptions(rowCountByStatus), [rowCountByStatus])
  const hasStatusFilterOptions = hasTripListStatusFilterOptions(allRows)
  const addDaysOrDatesLabel = intl.formatMessage({
    id: 'tCy9PycI1Guoepw6mk8q',
    defaultMessage: 'Add days or dates',
    description: 'Action text shown on a trip planner stop with no dates or day count',
  })
  const handlePriceSortClick = useCallback(() => {
    onSortToggle('price')
  }, [onSortToggle])

  const toggleDateFilter = useCallback(() => {
    if (!hasDateFilterOptions) return
    setDateFilterOpen(prev => !prev)
  }, [hasDateFilterOptions])

  const closeDateFilter = useCallback(() => {
    setDateFilterOpen(false)
  }, [])

  const handleDateFilterToggle = useCallback((date: string) => {
    const next = new Set(filters.dates)
    if (next.has(date)) {
      next.delete(date)
    } else {
      next.add(date)
    }
    onDateFiltersChange(next)
  }, [filters.dates, onDateFiltersChange])

  const handleClearDateFilters = useCallback(() => {
    onDateFiltersChange(new Set())
  }, [onDateFiltersChange])

  const toggleLocationFilter = useCallback(() => {
    if (!hasLocationFilterOptions) return
    setLocationFilterOpen(prev => !prev)
  }, [hasLocationFilterOptions])

  const closeLocationFilter = useCallback(() => {
    setLocationFilterOpen(false)
  }, [])

  const handleLocationFilterToggle = useCallback((location: string) => {
    const next = new Set(filters.locations)
    if (next.has(location)) {
      next.delete(location)
    } else {
      next.add(location)
    }
    onLocationFiltersChange(next)
  }, [filters.locations, onLocationFiltersChange])

  const handleClearLocationFilters = useCallback(() => {
    onLocationFiltersChange(new Set())
  }, [onLocationFiltersChange])

  const toggleTypeFilter = useCallback(() => {
    if (!hasTypeFilterOptions) return
    setTypeFilterOpen(prev => !prev)
  }, [hasTypeFilterOptions])

  const closeTypeFilter = useCallback(() => {
    setTypeFilterOpen(false)
  }, [])

  const handleTypeFilterToggle = useCallback((type: TripListRowType) => {
    const next = new Set(filters.types)
    if (next.has(type)) {
      next.delete(type)
    } else {
      next.add(type)
    }
    onTypeFiltersChange(next, filters.bookableOnly)
  }, [filters.bookableOnly, filters.types, onTypeFiltersChange])

  const handleBookableOnlyToggle = useCallback(() => {
    onTypeFiltersChange(new Set(filters.types), !filters.bookableOnly)
  }, [filters.bookableOnly, filters.types, onTypeFiltersChange])

  const handleClearTypeFilters = useCallback(() => {
    onTypeFiltersChange(new Set(), false)
  }, [onTypeFiltersChange])

  const toggleStatusFilter = useCallback(() => {
    if (!hasStatusFilterOptions) return
    setStatusFilterOpen(prev => !prev)
  }, [hasStatusFilterOptions])

  const closeStatusFilter = useCallback(() => {
    setStatusFilterOpen(false)
  }, [])

  const handleStatusFilterToggle = useCallback((status: TripListStatusValue) => {
    const next = new Set(filters.statuses)
    if (next.has(status)) {
      next.delete(status)
    } else {
      next.add(status)
    }
    onStatusFiltersChange(next)
  }, [filters.statuses, onStatusFiltersChange])

  const handleClearStatusFilters = useCallback(() => {
    onStatusFiltersChange(new Set())
  }, [onStatusFiltersChange])

  const handleClearAllFilters = useCallback(() => {
    setDateFilterOpen(false)
    setLocationFilterOpen(false)
    setTypeFilterOpen(false)
    setStatusFilterOpen(false)
    onClearFilters()
  }, [onClearFilters])

  const renderTotalAmount = useCallback((chunks: ReactNode) => (
    <BodyText as="strong" variant="large" weight="bold">{chunks}</BodyText>
  ), [])

  const typeLabelByType = useMemo<Record<TripListRowType, string>>(() => ({
    accommodation: getTripListTypeLabel(intl, 'accommodation'),
    experience: getTripListTypeLabel(intl, 'experience'),
    dining: getTripListTypeLabel(intl, 'dining'),
    flight: getTripListTypeLabel(intl, 'flight'),
    transport: getTripListTypeLabel(intl, 'transport'),
    cruise: getTripListTypeLabel(intl, 'cruise'),
    activity: getTripListTypeLabel(intl, 'activity'),
  }), [intl])

  const typeHeaderLabel = intl.formatMessage({
    id: 'RTLU_2os7TlW4Bu0fGMO',
    defaultMessage: 'Type',
    description: 'Type column header label in the trip planner list view',
  })

  const dateFilterLabel = intl.formatMessage({
    id: 'aas_-qQnld7hAbKqjrO4',
    defaultMessage: 'Date',
    description: 'Date filter chip label in the trip planner list view',
  })

  const locationFilterLabel = intl.formatMessage({
    id: 'X3S-4N1Qdxx3efgejC7X',
    defaultMessage: 'Location',
    description: 'Location column header label in the trip planner list view',
  })

  const statusFilterLabel = intl.formatMessage({
    id: 'BsOr1mxehY4HiiOzz7Jf',
    defaultMessage: 'Status',
    description: 'Status column header label in the trip planner list view',
  })

  const priceFilterLabel = intl.formatMessage({
    id: 'ChJyqLBRZb8uxapP1Izz',
    defaultMessage: 'Price',
    description: 'Price column header label in the trip planner list view',
  })

  useCloseDropdownWhenUnavailable(hasDateFilterOptions, setDateFilterOpen)
  useCloseDropdownWhenUnavailable(hasLocationFilterOptions, setLocationFilterOpen)
  useCloseDropdownWhenUnavailable(hasTypeFilterOptions, setTypeFilterOpen)
  useCloseDropdownWhenUnavailable(hasStatusFilterOptions, setStatusFilterOpen)

  return (
    <ListPanel>
      <ScrollArea ref={scrollAreaRef}>
        <Toolbar>
          <FilterChips>
            {hasDateFilterOptions && (
              <>
                <FilterChipButton
                  type="button"
                  onClick={toggleDateFilter}
                  ref={dateFilterTriggerRef}
                  className={hasActiveDateFilter ? 'is-active' : undefined}
                >
                  <FilterChipText>{dateFilterLabel}</FilterChipText>
                  {hasActiveDateFilter && <Caption variant="medium" weight="bold">{activeDateFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
                <FilterDropdownList
                  size="S"
                  open={dateFilterOpen}
                  onClose={closeDateFilter}
                  triggerRef={dateFilterTriggerRef}
                  anchorRef={dateFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <TypeFilterPanel>
                    <TypeFilterHeader>
                      <BodyText variant="large" weight="bold">
                        {intl.formatMessage({
                          id: 'G9V19vN5wDaRVYwvFTJ4',
                          defaultMessage: 'Filter',
                          description: 'Heading for the trip planner list type filter menu',
                        })}
                      </BodyText>
                    </TypeFilterHeader>
                    <TypeFilterContent>
                      <TypeFilterOptions>
                        {dateFilterOptions.map(({ value, label, count }) => (
                          <LocationFilterMenuItem
                            key={value}
                            value={value}
                            label={label}
                            count={count}
                            selected={filters.dates.has(value)}
                            onToggle={handleDateFilterToggle}
                          />
                        ))}
                      </TypeFilterOptions>
                    </TypeFilterContent>
                    <TypeFilterFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearDateFilters}>
                        {intl.formatMessage({
                          id: '2COWVNuz-2nRz6RX95dR',
                          defaultMessage: 'Clear',
                          description: 'Button label for clearing trip planner list filters',
                        })}
                      </ClearFilterButton>
                    </TypeFilterFooter>
                  </TypeFilterPanel>
                </FilterDropdownList>
              </>
            )}
            {hasLocationFilterOptions && (
              <>
                <FilterChipButton
                  type="button"
                  onClick={toggleLocationFilter}
                  ref={locationFilterTriggerRef}
                  className={hasActiveLocationFilter ? 'is-active' : undefined}
                >
                  <FilterChipText>{locationFilterLabel}</FilterChipText>
                  {hasActiveLocationFilter && <Caption variant="medium" weight="bold">{activeLocationFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
                <FilterDropdownList
                  size="S"
                  open={locationFilterOpen}
                  onClose={closeLocationFilter}
                  triggerRef={locationFilterTriggerRef}
                  anchorRef={locationFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <TypeFilterPanel>
                    <TypeFilterHeader>
                      <BodyText variant="large" weight="bold">
                        {intl.formatMessage({
                          id: 'G9V19vN5wDaRVYwvFTJ4',
                          defaultMessage: 'Filter',
                          description: 'Heading for the trip planner list type filter menu',
                        })}
                      </BodyText>
                    </TypeFilterHeader>
                    <TypeFilterContent>
                      <TypeFilterOptions>
                        {locationFilterOptions.map(({ location, count }) => (
                          <LocationFilterMenuItem
                            key={location.filterValue}
                            value={location.filterValue}
                            label={location.label}
                            count={count}
                            selected={filters.locations.has(location.filterValue)}
                            onToggle={handleLocationFilterToggle}
                          />
                        ))}
                      </TypeFilterOptions>
                    </TypeFilterContent>
                    <TypeFilterFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearLocationFilters}>
                        {intl.formatMessage({
                          id: '2COWVNuz-2nRz6RX95dR',
                          defaultMessage: 'Clear',
                          description: 'Button label for clearing trip planner list filters',
                        })}
                      </ClearFilterButton>
                    </TypeFilterFooter>
                  </TypeFilterPanel>
                </FilterDropdownList>
              </>
            )}
            {hasTypeFilterOptions && (
              <>
                <FilterChipButton
                  type="button"
                  onClick={toggleTypeFilter}
                  ref={typeFilterTriggerRef}
                  className={hasActiveTypeFilter ? 'is-active' : undefined}
                >
                  <FilterChipText>{typeHeaderLabel}</FilterChipText>
                  {hasActiveTypeFilter && <Caption variant="medium" weight="bold">{activeTypeFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
                <FilterDropdownList
                  size="S"
                  open={typeFilterOpen}
                  onClose={closeTypeFilter}
                  triggerRef={typeFilterTriggerRef}
                  anchorRef={typeFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <TypeFilterPanel>
                    <TypeFilterHeader>
                      <BodyText variant="large" weight="bold">
                        {intl.formatMessage({
                          id: 'G9V19vN5wDaRVYwvFTJ4',
                          defaultMessage: 'Filter',
                          description: 'Heading for the trip planner list type filter menu',
                        })}
                      </BodyText>
                    </TypeFilterHeader>
                    <TypeFilterContent>
                      <TypeFilterOptions>
                        {typeFilterOptions.map(type => (
                          <TypeFilterMenuItem
                            key={type}
                            type={type}
                            label={typeLabelByType[type]}
                            count={rowCountByType[type]}
                            selected={filters.types.has(type)}
                            onToggle={handleTypeFilterToggle}
                          />
                        ))}
                        {hasBookableOnlyFilterOption && (
                          <LocationFilterMenuItem
                            value="bookableOnly"
                            label={intl.formatMessage({
                              id: 'Z0lGZ1R9f0cNfrErpwD4',
                              defaultMessage: 'Bookable only',
                              description: 'Filter option label for showing only bookable rows in the trip planner list view',
                            })}
                            count={bookableRows.length}
                            selected={filters.bookableOnly}
                            onToggle={handleBookableOnlyToggle}
                          />
                        )}
                      </TypeFilterOptions>
                    </TypeFilterContent>
                    <TypeFilterFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearTypeFilters}>
                        {intl.formatMessage({
                          id: '2COWVNuz-2nRz6RX95dR',
                          defaultMessage: 'Clear',
                          description: 'Button label for clearing trip planner list filters',
                        })}
                      </ClearFilterButton>
                    </TypeFilterFooter>
                  </TypeFilterPanel>
                </FilterDropdownList>
              </>
            )}
            {hasStatusFilterOptions && (
              <>
                <FilterChipButton
                  type="button"
                  onClick={toggleStatusFilter}
                  ref={statusFilterTriggerRef}
                  className={hasActiveStatusFilter ? 'is-active' : undefined}
                >
                  <FilterChipText>{statusFilterLabel}</FilterChipText>
                  {hasActiveStatusFilter && <Caption variant="medium" weight="bold">{activeStatusFilterCount}</Caption>}
                  <LineAngleDownIcon size="XS" />
                </FilterChipButton>
                <FilterDropdownList
                  size="S"
                  open={statusFilterOpen}
                  onClose={closeStatusFilter}
                  triggerRef={statusFilterTriggerRef}
                  anchorRef={statusFilterTriggerRef}
                  placement="bottom-start"
                  portaled
                >
                  <TypeFilterPanel>
                    <TypeFilterHeader>
                      <BodyText variant="large" weight="bold">
                        {intl.formatMessage({
                          id: 'G9V19vN5wDaRVYwvFTJ4',
                          defaultMessage: 'Filter',
                          description: 'Heading for the trip planner list type filter menu',
                        })}
                      </BodyText>
                    </TypeFilterHeader>
                    <TypeFilterContent>
                      <TypeFilterOptions>
                        {statusFilterOptions.map(status => (
                          <StatusFilterMenuItem
                            key={status}
                            status={status}
                            label={getTripListStatusLabel(intl, status)}
                            count={rowCountByStatus[status]}
                            selected={filters.statuses.has(status)}
                            onToggle={handleStatusFilterToggle}
                          />
                        ))}
                      </TypeFilterOptions>
                    </TypeFilterContent>
                    <TypeFilterFooter>
                      <ClearFilterButton size="medium" kind="tertiary" onClick={handleClearStatusFilters}>
                        {intl.formatMessage({
                          id: '2COWVNuz-2nRz6RX95dR',
                          defaultMessage: 'Clear',
                          description: 'Button label for clearing trip planner list filters',
                        })}
                      </ClearFilterButton>
                    </TypeFilterFooter>
                  </TypeFilterPanel>
                </FilterDropdownList>
              </>
            )}
            <FilterChipButton
              type="button"
              onClick={handlePriceSortClick}
              className={sort?.field === 'price' ? 'is-active' : undefined}
              disabled={!hasVisibleRows}
              aria-label={intl.formatMessage({
                id: 'Nh7mkoTbbVKMqi-doe60',
                defaultMessage: 'Sort by price',
                description: 'Accessible label for the trip planner list view price column sort button',
              })}
            >
              <FilterChipText>{priceFilterLabel}</FilterChipText>
              {getSortIcon(sort, 'price')}
            </FilterChipButton>
            {hasActiveFilters && (
              <ClearFiltersButton
                size="medium"
                kind="secondary"
                startIcon={<LineTimesIcon size="S" />}
                onClick={handleClearAllFilters}
              >
                {intl.formatMessage({
                  id: 'BEzjrYXQeqoRk_KikB7D',
                  defaultMessage: 'Clear filters',
                  description: 'Menu item label for clearing list view filters in the trip planner list view',
                })}
              </ClearFiltersButton>
            )}
          </FilterChips>
          <ToolbarControls>
            <LuxPlusToggle>
              <LuxPlusLogoWrapper height={10} />
              <ProductPaletteProvider palette="lux-plus">
                <Switch checked={luxPlusEnabled} onChange={onLuxPlusToggle} disabled={!hasVisibleRows} />
              </ProductPaletteProvider>
            </LuxPlusToggle>
          </ToolbarControls>
        </Toolbar>
        {groups.length === 0 && (
          <EmptyState>
            <BodyText variant="medium" colour="light-foreground-alt">
              {intl.formatMessage({
                id: '_HhYADAgKgwTYQp743JX',
                defaultMessage: 'No itinerary items match these filters.',
                description: 'Empty state shown when the trip planner list view has no rows after filtering',
              })}
            </BodyText>
          </EmptyState>
        )}
        {groups.length > 0 && (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            autoScroll={autoScrollConfig}
          >
            <ContentStack>
              {orderedRenderableGroups.map(group => (
                <DayGroup key={group.id}>
                  {group.group.stopHeader && (
                    <StopHeader
                      data-stop-id={group.group.stopHeader.stop.id}
                      data-trip-list-group-target-id={group.id}
                      data-trip-list-group-target-placement="before"
                    >
                      <StopHeaderText>
                        <StopHeaderTitle variant="medium" weight="bold" lineClamp={1}>
                          {group.group.stopHeader.title}
                        </StopHeaderTitle>
                        <StopHeaderSubtitle variant="large">
                          <StopTimingEditControl
                            stop={group.group.stopHeader.stop}
                            tripStops={tripStops}
                            onSendMessage={onSendStopTimingEditMessage}
                            disabled={actionsDisabled}
                          >
                            {group.group.stopHeader.subtitle || addDaysOrDatesLabel}
                          </StopTimingEditControl>
                        </StopHeaderSubtitle>
                      </StopHeaderText>
                      {!group.group.showEmptyPlaceholder && group.group.stopSearchActions && (
                        <StopHeaderActions>
                          <StopSearchActions
                            actions={group.group.stopSearchActions}
                            stop={group.group.stopHeader.stop}
                            variant="dark"
                            onOpenStopHotels={onOpenStopHotels}
                            onOpenStopFlights={onOpenStopFlights}
                            onOpenStopExperiences={onOpenStopExperiences}
                          />
                        </StopHeaderActions>
                      )}
                    </StopHeader>
                  )}
                  {group.rows.map((rowView, rowIndex, rowsInGroup) => {
                    const placement = rowView.row.id === overDragRowId ? dropTargetPlacement : undefined
                    // Only show the insertion line between two tiles, not at a group's
                    // top/bottom edge (before the first row or after the last row).
                    const isGroupEdge =
                      (placement === 'before' && rowIndex === 0) ||
                      (placement === 'after' && rowIndex === rowsInGroup.length - 1)
                    return (
                      <TripListRowItem
                        key={rowView.row.id}
                        row={rowView.row}
                        tripStops={tripStops}
                        selected={rowView.selected}
                        showGroupMetadata={rowView.showGroupMetadata}
                        showLocationMetadata={rowView.showLocationMetadata}
                        firstRow={rowView.firstRow}
                        lastRow={rowView.lastRow}
                        luxPlusEnabled={luxPlusEnabled}
                        onSelectRow={onSelectRow}
                        onRowClick={onRowClick}
                        onBookRow={onBookRow}
                        onViewBooking={onViewBooking}
                        onAskAi={onAskAi}
                        onAskAiMove={onAskAiMove}
                        onDelete={onDelete}
                        onMoveToDay={onMoveToDay}
                        onUpdateNote={onUpdateNote}
                        onUpdateStatus={onUpdateStatus}
                        isBookingPending={rowView.isBookingPending}
                        isNoteUpdatePending={rowView.isNoteUpdatePending}
                        isStatusUpdatePending={rowView.isStatusUpdatePending}
                        notesDisabled={notesDisabled}
                        statusDisabled={statusDisabled}
                        actionsDisabled={actionsDisabled}
                        dragDisabled={dragActionsDisabled}
                        dropTargetPlacement={isGroupEdge ? undefined : placement}
                      />
                    )
                  })}
                  {group.group.showEmptyPlaceholder && (
                    <EmptyStopPlaceholder
                      data-trip-list-group-target-id={group.id}
                      data-trip-list-group-target-placement="after"
                    >
                      <EmptyStopPlaceholderText>
                        <BodyText variant="medium" colour="light-foreground-alt">
                          {intl.formatMessage({
                            id: 'dukav_e-eCPWiO_9pp-2',
                            defaultMessage: "You don't have anything planned in {placeName} yet",
                            description: 'Placeholder text for an empty stop in the trip planner list view',
                          }, {
                            placeName: group.group.stopHeader?.title ?? group.group.title,
                          })}
                        </BodyText>
                      </EmptyStopPlaceholderText>
                      <EmptyStopActions>
                        {group.group.stopHeader && group.group.stopSearchActions && (
                          <StopSearchActions
                            actions={group.group.stopSearchActions}
                            stop={group.group.stopHeader.stop}
                            variant="default"
                            onOpenStopHotels={onOpenStopHotels}
                            onOpenStopFlights={onOpenStopFlights}
                            onOpenStopExperiences={onOpenStopExperiences}
                          />
                        )}
                      </EmptyStopActions>
                    </EmptyStopPlaceholder>
                  )}
                </DayGroup>
              ))}
            </ContentStack>
            <DragOverlay
              dropAnimation={{
                duration: DROP_ANIMATION_DURATION,
                easing: 'ease-out',
                sideEffects: dropAnimationSideEffects,
              }}
              zIndex={zIndex.draggingItem}
            >
              {activeRenderableRow && (
                <DragOverlayContent>
                  <TripListRowItem
                    row={activeRenderableRow.row}
                    tripStops={tripStops}
                    selected={activeRenderableRow.selected}
                    showGroupMetadata={activeRenderableRow.showGroupMetadata}
                    showLocationMetadata={activeRenderableRow.showLocationMetadata}
                    firstRow={activeRenderableRow.firstRow}
                    lastRow={activeRenderableRow.lastRow}
                    luxPlusEnabled={luxPlusEnabled}
                    onSelectRow={onSelectRow}
                    onRowClick={onRowClick}
                    onBookRow={onBookRow}
                    onViewBooking={onViewBooking}
                    onAskAi={onAskAi}
                    onAskAiMove={onAskAiMove}
                    onDelete={onDelete}
                    onMoveToDay={onMoveToDay}
                    onUpdateNote={onUpdateNote}
                    onUpdateStatus={onUpdateStatus}
                    isBookingPending={activeRenderableRow.isBookingPending}
                    isNoteUpdatePending={activeRenderableRow.isNoteUpdatePending}
                    isStatusUpdatePending={activeRenderableRow.isStatusUpdatePending}
                    notesDisabled
                    statusDisabled
                    actionsDisabled
                    dragDisabled
                    isDragOverlay
                  />
                  <WhileDraggingStyle />
                </DragOverlayContent>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </ScrollArea>
      {selectedRows.length > 0 && (
        <TripListQuickActions
          selectedCount={selectedRows.length}
          disabled={actionsDisabled}
          moveDayOptions={selectedMoveDayOptions}
          showMoveWithAi={showSelectedMoveWithAi}
          onAskAi={onAskAiSelectedRows}
          onMoveToDay={onMoveSelectedRowsToDay}
          onMoveWithAi={onAskAiMoveSelectedRows}
          onDelete={onDeleteSelectedRows}
          onClear={onClearSelection}
        />
      )}
      {bookableRows.length > 0 && (
        <Footer>
          <FooterSummary direction="horizontal" gap={8} verticalAlign="center">
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: '3hjcizN16s5NKoi8v_PR',
                defaultMessage: 'Book through Luxury Escapes',
                description: 'Footer label describing rows that can be booked through Luxury Escapes',
              })}
            </BodyText>
            <CountChip>
              <Caption variant="large">
                {intl.formatMessage(
                  {
                    id: 'TtDyDrOLvud-zyDhBmK8',
                    defaultMessage: '{count, plural, one {# item} other {# items}}',
                    description: 'Footer count of bookable itinerary items in the trip planner list view',
                  },
                  { count: bookableRows.length },
                )}
              </Caption>
            </CountChip>
          </FooterSummary>
          <FooterActions direction="horizontal" gap={16} verticalAlign="center">
            {selectedRows.length > 0 && (
              <BodyText variant="medium" colour="light-foreground-alt">
                {intl.formatMessage(
                  {
                    id: 'jkxrIQ9bs7jzZzZg3nm_',
                    defaultMessage: '{count, plural, one {# item selected} other {# items selected}}',
                    description: 'Selected item count shown in the trip planner list footer',
                  },
                  { count: selectedRows.length },
                )}
              </BodyText>
            )}
            {footerTotal && (
              <BodyText variant="medium" colour="normal-contrast">
                {intl.formatMessage(
                  {
                    id: '3dVgNkGJWa2aJcRksMWS',
                    defaultMessage: 'Total <amount>{amountValue}</amount>',
                    description: 'Total selected row price shown in the trip planner list footer',
                  },
                  {
                    amountValue: <FormatCurrency value={footerTotal.total} currency={footerTotal.currency} format="roundedDollar" />,
                    amount: renderTotalAmount,
                  },
                )}
              </BodyText>
            )}
            <BookAllButton size="medium" kind="primary" disabled={actionsDisabled} onClick={onBookAll}>
              {intl.formatMessage({
                id: 'pRxQObCdrNpxcYUl2ccY',
                defaultMessage: 'Book all',
                description: 'CTA button label for booking selected rows in the trip planner list view',
              })}
            </BookAllButton>
          </FooterActions>
        </Footer>
      )}
    </ListPanel>
  )
}

export default TripListView
